import { BehaviorSubject, filter, take } from 'rxjs'
import { MainComponent } from '../components/consent-main/consent-main.component'
import { type SupportedLanguages } from '../models/app-translations.model'
import { initDocumentWriteDisable } from '../polyfills/document-write'
import { customEventPolyfill } from '../polyfills/event-polyfill'
import { initNodeListForeachPolyfill } from '../polyfills/nodelist-foreach'
import { AppConfigService } from './app-config.service'
import { DomPreparationService } from './dom-preparation.service'
import { TranslateService } from './translate.service'
import { RuntimeEnvType } from 'src/app/models/app-config.model'
import { CookieConsent } from 'src/app/models/consent-data.model'
import { GtagConsentService } from './gtag-consent.service'
import { ConsentDataService } from './consent-data.service'
import { AppEventService } from './app-event.service'
import { PublicConsentState } from 'app/models/api.model'

export class AppInitializerService {
  private static instance: AppInitializerService
  private readonly domPreparationService: DomPreparationService
  private readonly translateService: TranslateService
  private readonly appConfigService: AppConfigService
  private readonly gtagConsentService: GtagConsentService
  private readonly consentDataService: ConsentDataService
  private readonly appEventService: AppEventService
  private publicConsentState$!: BehaviorSubject<PublicConsentState | null>

  constructor() {
    this.domPreparationService = DomPreparationService.getInstance()
    this.translateService = TranslateService.getInstance()
    this.appConfigService = AppConfigService.getInstance()
    this.gtagConsentService = GtagConsentService.getInstance()
    this.consentDataService = ConsentDataService.getInstance()
    this.appEventService = AppEventService.getInstance()
    this.publicConsentState$ = new BehaviorSubject<PublicConsentState | null>(null)
  }

  static getInstance(): AppInitializerService {
    if (AppInitializerService.instance === undefined) {
      AppInitializerService.instance = new AppInitializerService()
    }

    return AppInitializerService.instance
  }

  setRuntimeEnvType(type: RuntimeEnvType): void {
    this.appConfigService.setRuntimeEnvType(type)
  }

  setConsentData(data: CookieConsent): void {
    this.appConfigService.setConsentData(data)
  }

  setSaveLogApiUrl(url: string): void {
    this.appConfigService.setSaveLogApiUrl(url)
  }

  setInterfaceLang(lang: SupportedLanguages): void {
    this.appConfigService.setInterfaceLang(lang)
  }

  setIntegrationWithGTM(delay?: number): void {
    this.appConfigService.setIntegrationWithGTM(delay)
    this.gtagConsentService.prepareForIntegration(delay)
  }

  displayStickyTrigger(visible = false): void {
    this.appConfigService.setDisplayStickyTriggerState(visible)
  }

  displayFirstStepRejectAllButton(visible = false): void {
    this.appConfigService.setDisplayFirstStepRejectAllButtonState(visible)
  }

  displayCloseButton(visible = false): void {
    this.appConfigService.setDisplayCloseButtonState(visible)
  }

  addCustomCssClass(className: string): void {
    this.appConfigService.setCustomCssClass(className)
  }

  init(): void {
    this.initTranslateService()
    this.initMainApp()
    this.addConsentChangeSubscription()
  }

  getPublicConsentDataSubscription(): BehaviorSubject<PublicConsentState | null> {
    return this.publicConsentState$
  }

  checkConsent(consentTag: string): boolean {
    const parsedTags = consentTag.split(',').map(tag => tag.trim())
    const consents = this.publicConsentState$.value

    if (!consents) {
      return false
    }

    for (const tag of parsedTags) {
      if (!this.checkConsentByTag(tag, consents)) {
        return false
      }
    }

    return true
  }

  private checkConsentByTag(tag: string, consents: PublicConsentState): boolean {
    return this.checkConsentStatusByCategory(tag, consents) || this.checkConsentStatusByItem(tag, consents)
  }

  private checkConsentStatusByCategory(categoryTag: string, consents: PublicConsentState): boolean {
    return Boolean(consents[categoryTag]?.accept)
  }

  private checkConsentStatusByItem(itemTag: string, consents: PublicConsentState) {
    const flatItems = Object.values(consents).flatMap(category => category.items)
    const specificItems = flatItems.filter(item => item.tag === itemTag)
    const specificItemCategories = specificItems.flatMap(item => item.category)

    for (const category of specificItemCategories) {
      if (!this.checkConsentStatusByCategory(category, consents)) {
        return false
      }
    }

    return true
  }

  private loadPolyfills(): void {
    initDocumentWriteDisable()
    initNodeListForeachPolyfill()
    customEventPolyfill()
  }

  private initTranslateService(): void {
    this.translateService.init()
  }

  private initMainApp(): void {
    const runtimeEnv = this.appConfigService.getRuntimeEnvType()
    this.createEntrypointHtmlElementForESM()

    switch (runtimeEnv) {
      case 'esm':
        this.initEsmApp()
        break
      case 'umd':
        this.initUmdApp()
        break
      default:
        this.initUmdApp()
        break
    }
  }

  private initEsmApp(): void {
    this.translateService.ready$.pipe(take(1)).subscribe(() => {
      this.domPreparationService.runStrictElements()
      const main = new MainComponent()
      main.initApp()
    })
  }

  private initUmdApp(): void {
    this.domPreparationService.runMainScript().subscribe(() => {
      this.translateService.ready$.pipe(take(1)).subscribe(() => {
        this.loadPolyfills()
        this.domPreparationService.runStrictElements()
        const main = new MainComponent()
        main.initApp()
      })
    })
  }

  private createEntrypointHtmlElementForESM(): void {
    const dataComponentName = this.appConfigService.getEntrypointHTMLElementName()
    const div = document.createElement('div')
    div.setAttribute('data-component', dataComponentName)
    document.body.appendChild(div)
  }

  private addConsentChangeSubscription(): void {
    this.consentDataService.consentData$.pipe(filter(data => data !== null)).subscribe(data => {
      this.emitPublicConsentState(data as CookieConsent)
      this.refreshConsentsInGTM(data as CookieConsent)
      this.appEventService.emitConsentChangeEvent()
    })
  }

  private emitPublicConsentState(data: CookieConsent): void {
    if (!this.publicConsentState$) {
      return
    }

    const categories: PublicConsentState = {}

    data.categories.forEach(category => {
      categories[category.tag] = {
        accept: category.accept,
        items: category.items.map(item => {
          return {
            name: item.name,
            tag: item.tag,
            category: category.tag
          }
        })
      }
    })

    this.publicConsentState$.next(categories)
  }

  private refreshConsentsInGTM(data: CookieConsent): void {
    if (this.appConfigService.getIntegrationWithGTM().enable) {
      this.gtagConsentService.refreshGoogleConsents(data)
    }
  }
}
